import React from 'react';
import classNames from 'classnames';
import scrollIntoView from 'scroll-into-view-if-needed';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { OverlayContext } from 'ui/utils/OverlayManager';
import { D3TooltipSkill } from 'tooltips';
import EditingContext from 'store/editingContext';
import { SkillIcon, SkillHeader, SkillActive, SkillInfo, SkillParams } from './skillInfo';
import { getStats } from 'logic/stats';
import { getAttackType, getSkillBonus } from 'logic/skills';
import { profileSetSkill } from 'logic/profileReducers';
import { useDragDrop, useSkillMover } from 'components/dragDrop';

const _L = Locale("ui-skills.js");

const skillLevels = [1, 2, 4, 9, 14, 19];



function SkillPicker({ onClose, index }) {
  const dispatch = Store.useDispatch();
  const charClass = Store.useProfile(profile => profile.class);
  const skills = Store.useProfile(profile => profile.skills);
  const skill = skills[index];
  const info = skill && Data.skills[charClass][skill[0]];

  const used = {}, exclusive = {};
  for (const sk of skills) {
    const curInfo = sk && Data.skills[charClass][sk[0]];
    if (curInfo && curInfo !== info) {
      used[sk[0]] = true;
      if (curInfo.exclusive) {
        exclusive[curInfo.exclusive] = sk[0];
      }
    }
  }

  const setRune = rune => {
    dispatch.setProfile(profile => profileSetSkill(profile, index, [skill[0], rune]));
    onClose();
  };

  return (
    <div className={classNames("d3p-SkillPicker", {"d3p-expanded": !!info})}>
      <div className="d3p-content">
        {Object.entries(Data.skills[charClass]).map(([id, info]) => {
          let disabled = used[id];
          if (info.exclusive && exclusive[info.exclusive] && exclusive[info.exclusive] !== id) {
            disabled = true;
          }
          if (index === 0 && info.nolmb) {
            disabled = true;
          }
          const style = {
            backgroundPosition: `${-info.col}em ${-2 * info.row - (disabled ? 1 : 0)}em`,
            left: 10 + 48 * info.col,
            top: 10 + 48 * info.row,
          };
          const onClick = disabled ? null : () => dispatch.setProfile(profile => profileSetSkill(profile, index, [id, "x"]));
          return (
            <D3TooltipSkill key={id} charClass={charClass} skill={id} touchAction="none">
              <span className={classNames(`d3p-skill-icon d3p-class-${charClass}`, {"d3p-selected": skill && id === skill[0]})} style={style} onClick={onClick}>
                <span className="d3p-skill-frame"/>
              </span>
            </D3TooltipSkill>
          );
        })}
        {!!info && (
          <ul className="d3p-rune-list">
            <D3TooltipSkill charClass={charClass} skill={skill[0]} touchAction="none">
              <li onClick={() => setRune("x")} className={"x" === skill[1] ? "d3p-selected" : null}><span className="d3p-rune d3p-rune-large d3p-rune-x"/>{_L`No Rune`}</li>
            </D3TooltipSkill>
            {Object.entries(info.runes).map(([rune, name]) => (
              <D3TooltipSkill key={rune} charClass={charClass} skill={skill[0]} rune={rune} onlyRune touchAction="none">
                <li onClick={() => setRune(rune)} className={rune === skill[1] ? "d3p-selected" : null}>
                  <span className={`d3p-rune d3p-rune-large d3p-rune-${rune}`}/>
                  <span className="d3p-rune-name">{name}</span>
                </li>
              </D3TooltipSkill>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const AttackCard = React.forwardRef(function AttackCard({ drop, dragClass, index, editing, picker }, ref) {
  const type = Store.useProfile(getAttackType);
  const info = Data.skills.attack[type];

  return (
    <div className={classNames("d3p-SkillCard d3p-skill", dragClass, {"d3p-editing": editing})} ref={drop}>
      <span className={`d3p-skill-key d3p-skill-${index}`}/>
      <D3TooltipSkill charClass="attack" skill={type} touchAction="none">
        <SkillHeader icon={<SkillIcon charClass="attack" info={info}/>}
                     name={info.name}
                     onClick={picker}
                     ref={ref}
        >
          <SkillInfo type="attack" id={type}/>
        </SkillHeader>
      </D3TooltipSkill>
    </div>
  );
});

const SkillActiveBox = React.memo(function SkillActiveBox({ charClass, skill, info }) {
  const state = Store.useState();
  const stats = getStats(state);

  if (info.activeshow) {
    if (!info.activeshow(skill[1], stats)) {
      return null;
    }
  } else {
    if (!getSkillBonus(charClass, skill, stats)) {
      return null;
    }
  }

  return <SkillActive info={info}/>;
});

export const SkillCard = React.memo(function SkillCard({ index }) {
  const [editing, setEditing] = React.useState(false);
  const attackType = Store.useProfile(getAttackType);
  const charClass = Store.useProfile(profile => profile.class);
  const dispatch = Store.useDispatch();
  const overlays = React.useContext(OverlayContext);
  const picker = React.useCallback(e => {
    setEditing(true);
    overlays.popup(e, <SkillPicker index={index}/>).finally(() => setEditing(false));
  }, [overlays, index]);

  const skill = Store.useProfile(profile => profile.skills[index]);
  let info = skill && Data.skills[charClass][skill[0]];

  const onClear = React.useCallback(e => {
    dispatch.setProfile(profile => profileSetSkill(profile, index, null));
    e.preventDefault();
  }, [dispatch, index]);

  const headerRef = React.useRef();
  const onEdit = React.useCallback(({ type, index: targetIndex }) => {
    if (type === "skill" && index === targetIndex) {
      scrollIntoView(headerRef.current, {scrollMode: "if-needed", block: "center", inline: "center"});
      const rect = headerRef.current.getBoundingClientRect();
      picker({clientX: (rect.left + rect.right) / 2, clientY: (rect.top + rect.bottom) / 2});
      return true;
    }
  }, [index, picker]);
  EditingContext.useHandler(onEdit);

  const skillMover = useSkillMover();
  const [dragClass, drag, drop] = useDragDrop("SKILL", {
    canDrag: () => info != null,
    options: {dropEffect: "move"},
    begin: () => ({type: "SKILL", charClass, id: skill[0], index}),
    end: (item, monitor) => skillMover({index}, monitor.getDropResult()),
  }, {
    canDrop: item => {
      if (item.type !== "SKILL") return false;
      if (index === 0 && Data.skills[charClass][item.id]?.nolmb) return false;
      if (item.index === 0 && info?.nolmb) return false;
      return true;
    },
    drop: () => ({index}),
  });

  if (!skill && (index === 0 || index === 1)) {
    return <AttackCard index={index} editing={editing} picker={picker} ref={headerRef} drop={drop} dragClass={dragClass}/>;
  }

  let displayClass = charClass;
  let displayInfo = dragClass === "d3p-dragging" ? null : info;
  if (!displayInfo && (index === 0 || index === 1)) {
    displayClass = "attack";
    displayInfo = Data.skills.attack[attackType];
  }

  return (
    <div className={classNames("d3p-SkillCard d3p-skill", dragClass, {"d3p-editing": editing})} ref={drop}>
      <span className={`d3p-skill-key d3p-skill-${index}`}/>
      <D3TooltipSkill charClass={charClass} skill={info && skill[0]} rune={info && skill[1]} disabled={!displayInfo || dragClass === "d3p-dragging"} touchAction="none">
        <SkillHeader icon={<SkillIcon charClass={displayClass} info={displayInfo} alt={skillLevels[index]} ref={drag}/>}
                     empty={!info}
                     name={info ? info.name : _L`Choose Skill`}
                     onClick={picker}
                     onContextMenu={info && onClear}
                     ref={headerRef}
        >
          {!!info && (
            <span className="d3p-skill-rune">
              <span className={`d3p-rune d3p-rune-${skill[1]}`}/>{" "}
              {info.runes[skill[1]] || _L`No Rune`}
            </span>
          )}
        </SkillHeader>
      </D3TooltipSkill>
      {!!info && (
        <div className="d3p-skill-extra">
          {!!info.setActive && <SkillActiveBox charClass={charClass} skill={skill} info={info}/>}
          {!!info.params && <SkillParams info={info} skill={skill}/>}
          {!!info.info && <SkillInfo type="skill" id={skill[0]} rune={skill[1]}/>}
        </div>
      )}
    </div>
  );
});

export default function SkillsSection() {
  return (
    <React.Fragment>
      {[0, 1, 2, 3, 4, 5].map(index => <SkillCard key={index} index={index}/>)}
    </React.Fragment>
  );
}
